import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Upload,
  Show,
  TickSquare,
  TimeCircle,
  Danger,
  Document,
  Download
} from 'react-iconly';
import { formatUploadDate } from '../lib/dateUtils';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';
const ERROR_RED = '#F44336';

// Document requirements by food category
const DOCUMENT_REQUIREMENTS = {
  'shelf-stable': [
    {
      id: 'cottage-food-cert',
      name: 'Cottage Food Law Certificate',
      description: 'Required for selling homemade baked goods and shelf-stable foods',
      required: true,
      fileTypes: ['pdf', 'jpg', 'png'],
      maxSize: 10 // MB
    }
  ],
  'perishable-meals': [
    {
      id: 'food-handler-cert',
      name: 'Food Handler Certificate',
      description: 'Required for handling and preparing perishable foods',
      required: true,
      fileTypes: ['pdf', 'jpg', 'png'],
      maxSize: 10
    },
    {
      id: 'kitchen-lease',
      name: 'Commercial Kitchen Lease Proof',
      description: 'Proof of access to a licensed commercial kitchen',
      required: true,
      fileTypes: ['pdf', 'jpg', 'png'],
      maxSize: 10
    }
  ],
  'catering': [
    {
      id: 'food-handler-cert-catering',
      name: 'Food Handler Certificate',
      description: 'Required for preparing food in client homes',
      required: true,
      fileTypes: ['pdf', 'jpg', 'png'],
      maxSize: 10
    }
  ]
};

// Status configurations
const STATUS_CONFIG = {
  approved: {
    label: 'Approved',
    color: SUCCESS_GREEN,
    icon: TickSquare,
    bgColor: '#E8F5E8'
  },
  pending: {
    label: 'Pending Review',
    color: WARNING_ORANGE,
    icon: TimeCircle,
    bgColor: '#FFF3E0'
  },
  expired: {
    label: 'Expired',
    color: ERROR_RED,
    icon: Danger,
    bgColor: '#FFEBEE'
  },
  needsResubmission: {
    label: 'Needs Resubmission',
    color: ERROR_RED,
    icon: Danger,
    bgColor: '#FFEBEE'
  },
  notUploaded: {
    label: 'Not Uploaded',
    color: ACCENT_GRAY,
    icon: Document,
    bgColor: '#F5F5F5'
  }
};

export function DocumentCenter() {
  const { currentUser } = useAuth();
  const [sellerProfile, setSellerProfile] = useState(null);
  const [documents, setDocuments] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploadModal, setUploadModal] = useState({ show: false, documentId: null });
  const [viewModal, setViewModal] = useState({ show: false, document: null });
  const [uploading, setUploading] = useState(false);

  // Get required documents for seller's food category
  const getRequiredDocuments = () => {
    if (!sellerProfile?.foodType) return [];
    
    // Map food types to document requirements
    let category = sellerProfile.foodType;
    
    // Handle special case for catering/in-home chef
    if (sellerProfile.foodType === 'perishable-meals' && sellerProfile.cookingLocation === 'client-home') {
      category = 'catering';
    }
    
    return DOCUMENT_REQUIREMENTS[category] || [];
  };

  // Get document status
  const getDocumentStatus = (documentId) => {
    const doc = documents[documentId];
    if (!doc) return 'notUploaded';
    return doc.status || 'pending';
  };

  // Handle file upload
  const handleFileUpload = async (file, documentId) => {
    if (!file) return;

    // Validate file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const requiredDoc = getRequiredDocuments().find(d => d.id === documentId);
    
    if (!requiredDoc.fileTypes.includes(fileExtension)) {
      alert(`Please upload a ${requiredDoc.fileTypes.join(', ')} file`);
      return;
    }

    // Validate file size (MB)
    if (file.size > requiredDoc.maxSize * 1024 * 1024) {
      alert(`File size must be less than ${requiredDoc.maxSize}MB`);
      return;
    }

    setUploading(true);
    
    try {
      // TODO: Implement actual file upload to Firebase Storage
      // For now, simulate upload
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update documents state
      const newDocument = {
        id: documentId,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadDate: new Date().toISOString(),
        status: 'pending',
        fileUrl: URL.createObjectURL(file) // Temporary URL for demo
      };
      
      setDocuments(prev => ({
        ...prev,
        [documentId]: newDocument
      }));
      
      setUploadModal({ show: false, documentId: null });
      
      // TODO: Save to Firebase
      console.log('Document uploaded:', newDocument);
      
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Handle document view/download
  const handleViewDocument = (document) => {
    if (document.fileUrl) {
      setViewModal({ show: true, document });
    }
  };

  // Load seller profile and documents
  useEffect(() => {
    const loadSellerData = async () => {
      try {
        // TODO: Load seller profile from Firebase
        // For now, use mock data
        const mockProfile = {
          foodType: 'perishable-meals',
          cookingLocation: 'commercial-kitchen'
        };
        
        setSellerProfile(mockProfile);
        
        // TODO: Load existing documents from Firebase
        // For now, use empty state
        setDocuments({});
        
      } catch (error) {
        console.error('Failed to load seller data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      loadSellerData();
    }
  }, [currentUser]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C97B53] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  const requiredDocuments = getRequiredDocuments();

  if (requiredDocuments.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <p className="text-gray-600">No documents required for your current food category.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Document Center</h2>
        <p className="text-gray-600">
          Upload and track the status of required compliance documents for your food category.
        </p>
      </div>

      {/* Documents List */}
      <div className="space-y-4">
        {requiredDocuments.map((doc) => {
          const status = getDocumentStatus(doc.id);
          const statusConfig = STATUS_CONFIG[status];
          const StatusIcon = statusConfig.icon;
          const existingDoc = documents[doc.id];

          return (
            <div key={doc.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{doc.name}</h3>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: statusConfig.bgColor,
                        color: statusConfig.color
                      }}
                    >
                      <StatusIcon size={14} />
                      {statusConfig.label}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-3">{doc.description}</p>
                  
                  {existingDoc && (
                    <div className="text-sm text-gray-500">
                      <p>Uploaded: {formatUploadDate(existingDoc.uploadDate)}</p>
                      <p>File: {existingDoc.name}</p>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2">
                  {existingDoc ? (
                    <>
                      <button
                        onClick={() => handleViewDocument(existingDoc)}
                        className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C97B53]"
                      >
                        <Show size={16} />
                        View
                      </button>
                      <button
                        onClick={() => setUploadModal({ show: true, documentId: doc.id })}
                        className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C97B53]"
                      >
                        <Upload size={16} />
                        Replace
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setUploadModal({ show: true, documentId: doc.id })}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#C97B53] border border-transparent rounded-md text-sm font-medium text-white hover:bg-[#B86A42] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C97B53]"
                    >
                      <Upload size={16} />
                      Upload
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Upload Modal */}
      {uploadModal.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Upload {getRequiredDocuments().find(d => d.id === uploadModal.documentId)?.name}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select File
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFileUpload(file, uploadModal.documentId);
                      }
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#C97B53] file:text-white hover:file:bg-[#B86A42]"
                  />
                </div>
                
                <div className="text-xs text-gray-500">
                  <p>Accepted formats: PDF, JPG, PNG</p>
                  <p>Maximum size: 10MB</p>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setUploadModal({ show: false, documentId: null })}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewModal.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {viewModal.document.name}
              </h3>
              
              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                                        <p><strong>Upload Date:</strong> {formatUploadDate(viewModal.document.uploadDate)}</p>
                  <p><strong>File Size:</strong> {(viewModal.document.size / 1024 / 1024).toFixed(2)} MB</p>
                  <p><strong>Status:</strong> {STATUS_CONFIG[viewModal.document.status]?.label}</p>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      // TODO: Implement actual file download
                      const link = document.createElement('a');
                      link.href = viewModal.document.fileUrl;
                      link.download = viewModal.document.name;
                      link.click();
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#C97B53] border border-transparent rounded-md text-sm font-medium text-white hover:bg-[#B86A42]"
                  >
                    <Download size={16} />
                    Download
                  </button>
                  
                  <button
                    onClick={() => setViewModal({ show: false, document: null })}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C97B53] mx-auto mb-4"></div>
              <p className="text-gray-600">Uploading document...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
